import { bool, func, number, oneOfType, shape, string } from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';

import Typography from 'ravenjs/lib/Typography';
import Col from 'ravenjs/lib/Col';
import { get } from 'ravenjs/utils/lodash';
import Form from 'ravenjs/lib/Form';
import { addSchemaValue, callFunc, genID } from 'ravenjs/utils';
import Row from 'ravenjs/lib/Row';
import { renderCheck } from 'ravenjs/utils/viewport';

import { CloseIcon } from 'components/Icons';
import WelcomeEmailPreview from 'components/WelcomeEmailPreview';
import { DEFAULT_COLORS } from 'constants/colors';
import { MODALS } from 'constants/modals';
import MESSAGES from 'constants/messages';
import { actions as uiActions, selectors as uiSelectors } from 'modules/ui';
import { sanitizeMarkup } from 'utils/sanitize';
import _cloneDeep from 'lodash/cloneDeep';
import schema from './schema.json';

import Modal from '../Modal';

const CloseIconWrapper = styled.div`
    align-items: flex-start;
    cursor: pointer;
    display: flex;
`;

const CustomHeaderStyled = styled.div`
    background-color: ${DEFAULT_COLORS.WHITE};
    border-bottom: 1px solid ${DEFAULT_COLORS.DARK};
    padding: 1rem;
    display: flex;
    justify-content: space-between;
    width: 100%;
    padding-left: 1.5rem;
`;

const WelcomeEmailPreviewModal = props => {
    const { closeModal, modal, isHybridSsoEnabled } = props;
    const selectedRole = get(modal, 'data.selectedRole', {});
    const [formData] = React.useState({});
    const open = get(modal, 'open', false);
    const body = get(modal, 'data.body', '');
    const title = get(modal, 'data.title', '');
    const to = get(modal, 'data.to', '');
    const from = get(modal, 'data.from', '');
    const subject = get(modal, 'data.subject', '');
    const footer = get(modal, 'data.footer', '');
    const headerLogo = get(modal, 'data.headerLogo', '');
    const defaultLogo = get(modal, 'data.defaultLogo', '');
    const customLogo = get(modal, 'data.customLogo', '');
    const loginInstruction = get(modal, 'data.loginInstruction', '');
    const loginLink = get(modal, 'data.loginLink', '');
    const createPasswordLink = get(modal, 'data.createPasswordLink', '');
    const partnerId = get(modal, 'data.partnerId', '');
    const welcomeEmailEnabledRoles = get(modal, 'data.welcomeEmailEnabledRoles', []);
    const disableCreatePasswordLink = get(modal, 'data.disableCreatePasswordLink', false);
    const updatePreviewForRole = get(modal, 'data.updatePreviewForRole', () => {});

    const getFormSchema = () => {
        const formSchema = _cloneDeep(schema);
        addSchemaValue(
            formSchema,
            'schema.properties.roleId.enumNames',
            welcomeEmailEnabledRoles?.map(({ description }) => description)
        );
        addSchemaValue(
            formSchema,
            'schema.properties.roleId.enum',
            welcomeEmailEnabledRoles?.map(({ id }) => id)
        );
        addSchemaValue(formSchema, 'schema.properties.roleId.default', selectedRole.id);
        return formSchema;
    };

    const onClickCloseIcon = () => {
        closeModal(MODALS.WELCOME_EMAIL_PREVIEW);
    };

    const renderContent = () => {
        const template = {
            body,
            footer,
            headerLogo,
            defaultLogo,
            customLogo,
            loginInstruction,
            loginLink,
            createPasswordLink,
        };
        return (
            <WelcomeEmailPreview
                key={genID()}
                emailTemplate={template}
                partnerId={partnerId}
                disableCreatePasswordLink={disableCreatePasswordLink}
            />
        );
    };

    const onFormChange = ({ formData: data }) => {
        if (formData.roleId !== data.roleId) {
            callFunc(updatePreviewForRole, data.roleId);
        }
    };

    const getCustomHeader = () => {
        return (
            <CustomHeaderStyled>
                <Row direction="column">
                    <Col>
                        <Typography
                            dangerouslySetInnerHTML={sanitizeMarkup(title)}
                            gutterBottom="0"
                            gutterTop="0"
                            fontSize="1.75rem"
                            padding="0.5rem 0"
                            type="subheading"
                            color={DEFAULT_COLORS.BLACK}
                        />
                    </Col>
                    {isHybridSsoEnabled && (
                        <Col>
                            <Form
                                {...getFormSchema()}
                                enableDirtyCheck
                                externalSubmission
                                formData={formData}
                                liveValidate
                                onChange={onFormChange}
                                showErrorList={false}
                            />
                        </Col>
                    )}
                    <Col>
                        <Typography fontWeight="700" fontSize="1rem" type="subheader">
                            {MESSAGES.EMAIL_PREVIEW.HEADER.TO}{' '}
                        </Typography>
                        <Typography fontSize="1rem" type="subheader">
                            {to}
                        </Typography>
                    </Col>
                    <Col>
                        <Typography fontWeight="700" fontSize="1rem" type="subheader">
                            {MESSAGES.EMAIL_PREVIEW.HEADER.FROM}{' '}
                        </Typography>
                        <Typography fontSize="1rem" type="subheader">
                            {from}
                        </Typography>
                    </Col>
                    <Col>
                        <Typography fontWeight="700" fontSize="1rem" type="subheader">
                            {MESSAGES.EMAIL_PREVIEW.HEADER.SUBJECT}{' '}
                        </Typography>
                        <Typography fontSize="1rem" type="subheader">
                            {subject}
                        </Typography>
                    </Col>
                </Row>
                <CloseIconWrapper onClick={onClickCloseIcon}>
                    <CloseIcon />
                </CloseIconWrapper>
            </CustomHeaderStyled>
        );
    };

    return (
        <Modal
            hideActions
            id={MODALS.WELCOME_EMAIL_PREVIEW}
            actions={[]}
            content={renderContent()}
            onClickCloseIcon={onClickCloseIcon}
            open={open}
            CustomHeader={getCustomHeader()}
            showFullView={renderCheck('md', 'less')}
            title={title}
            modalBodyProps={{
                backgroundColor: DEFAULT_COLORS.WHITE,
                borderBottomLeftRadius: '4px',
                borderBottomRightRadius: '4px',
                paddingTop: '0',
                overflow: 'initial',
            }}
            modalHeaderProps={{
                border: 'none',
                fontSize: '1.75rem',
                paddingBottom: '1.5rem',
                paddingTop: '1.5rem',
            }}
        />
    );
};

WelcomeEmailPreviewModal.propTypes = {
    closeModal: func.isRequired,
    isHybridSsoEnabled: bool.isRequired,
    modal: shape({
        data: shape({
            body: string,
            to: string,
            from: string,
            subject: string,
            title: string,
            partnerId: oneOfType([number, string]),
        }),
        open: bool,
    }).isRequired,
};

const mapDispatchToProps = {
    closeModal: uiActions.closeModal,
};

const mapStateToProps = () =>
    createStructuredSelector({
        modal: uiSelectors.getModalById(MODALS.WELCOME_EMAIL_PREVIEW),
        isHybridSsoEnabled: uiSelectors.isHybridSsoEnabled,
    });

export { WelcomeEmailPreviewModal as WelcomeEmailPreviewUnwrapped };
export default connect(mapStateToProps, mapDispatchToProps)(WelcomeEmailPreviewModal);
